import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import { EntitlementStatusBadge } from '@/modules/trials/components/EntitlementStatusBadge';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef } from '@/shared/components/ui/DataTable';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { Input } from '@/shared/components/ui/input';
import { formatMoney } from '@/shared/utils/money';
import { InstapayReviewDetailDrawer } from '../components/InstapayReviewDetailDrawer';
import { listAdminInstapayReviews } from '../services/adminInstapayReviews.api';
import type {
  AdminInstapayReviewItem,
  InstapayReviewStatus,
  ListAdminInstapayReviewsQuery,
} from '../types/adminInstapayReview';
import { INSTAPAY_REVIEW_STATUS_OPTIONS } from '../types/adminInstapayReview';
import { formatDateTime } from '../utils/paymentHelpers';

function formatTempAccess(
  item: AdminInstapayReviewItem,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (item.temporary_access_active && item.temporary_access_remaining_seconds != null) {
    const minutes = Math.ceil(item.temporary_access_remaining_seconds / 60);
    return t('payments.instapay.tempAccess.remaining', { minutes });
  }
  if (item.payment_status === 'temp_expired' || item.temporary_access_expires_at) {
    return t('payments.instapay.tempAccess.expired');
  }
  return t('payments.instapay.tempAccess.none');
}

export function InstapayReviewsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;
  const canReview = user?.permissions.includes('payments.review') ?? false;

  const [statusFilter, setStatusFilter] = useState<InstapayReviewStatus | ''>('pending_review');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedReview, setSelectedReview] = useState<AdminInstapayReviewItem | null>(null);

  useEffect(() => {
    if (user && !isSuperAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [user, isSuperAdmin, navigate]);

  const serverQuery = useMemo((): ListAdminInstapayReviewsQuery => {
    const q: ListAdminInstapayReviewsQuery = { limit: 100 };
    if (statusFilter) q.status = statusFilter;
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    return q;
  }, [statusFilter, debouncedSearch]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'payments', 'instapay', 'reviews', serverQuery],
    queryFn: () => listAdminInstapayReviews(serverQuery),
    enabled: isSuperAdmin,
  });

  const reviews = useMemo(() => data?.items ?? [], [data?.items]);
  const unavailable = t('payments.identity.unavailable');

  const columns: ColumnDef<AdminInstapayReviewItem>[] = useMemo(
    () => [
      {
        key: 'customer',
        header: t('payments.columns.customer'),
        accessor: (row) => (
          <div className="space-y-0.5 min-w-0">
            <TrialTruncatedText
              value={row.customer_name}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
              className="font-medium"
            />
            <TrialTruncatedText
              value={row.tenant_name}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
              className="text-xs text-muted-foreground"
            />
          </div>
        ),
      },
      {
        key: 'product',
        header: t('payments.columns.product'),
        accessor: (row) => (
          <div className="min-w-0">
            <TrialTruncatedText
              value={row.product_name}
              fallback={unavailable}
              maxWidthClass="max-w-[8rem]"
            />
            <p className="text-xs text-muted-foreground">{row.plan_name}</p>
          </div>
        ),
      },
      {
        key: 'amount',
        header: t('payments.amount'),
        accessor: (row) => (
          <span className="font-medium whitespace-nowrap">
            {formatMoney(row.amount, row.currency)}
          </span>
        ),
      },
      {
        key: 'reviewStatus',
        header: t('payments.instapay.columns.reviewStatus'),
        accessor: (row) => (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs whitespace-nowrap">
            {t(`payments.instapay.reviewStatus.${row.payment_status}`, row.payment_status)}
          </span>
        ),
      },
      {
        key: 'tempAccess',
        header: t('payments.instapay.columns.tempAccess'),
        accessor: (row) => (
          <span
            className={
              row.temporary_access_active
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-muted-foreground'
            }
          >
            {formatTempAccess(row, t)}
          </span>
        ),
      },
      {
        key: 'subscription',
        header: t('payments.columns.subscriptionStatus'),
        accessor: (row) => (
          <div className="space-y-1">
            <SubscriptionStatusBadge status={row.subscription_status} />
            <EntitlementStatusBadge status={row.entitlement_status} />
          </div>
        ),
      },
      {
        key: 'created',
        header: t('payments.columns.created'),
        accessor: (row) => (
          <span className="text-sm whitespace-nowrap">
            {formatDateTime(row.created_at, locale)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('payments.columns.actions'),
        accessor: (row) => (
          <Button variant="outline" size="sm" onClick={() => setSelectedReview(row)}>
            <Icons.Eye className="h-3.5 w-3.5" />
            {t('common.view')}
          </Button>
        ),
        align: 'right',
      },
    ],
    [t, locale, unavailable],
  );

  if (!user || !isSuperAdmin) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHero
        title={t('payments.instapay.title')}
        subtitle={t('payments.instapay.description')}
        eyebrow={t('payments.instapay.eyebrow')}
        icon={<Icons.DollarSign className="h-7 w-7" />}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/dashboard/payments"
              className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-accent/10"
            >
              {t('payments.instapay.backToPayments')}
            </Link>
            <Link
              to="/dashboard/payments/myfatoorah"
              className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-accent/10"
            >
              {t('payments.myfatoorah.linkFromPayments')}
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <Icons.RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </Button>
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1.5 md:col-span-2">
            <label htmlFor="instapay-search" className="text-xs font-medium text-muted-foreground">
              {t('payments.filters.search')}
            </label>
            <div className="flex gap-2">
              <Input
                id="instapay-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('payments.filters.searchPlaceholder')}
              />
              <Button variant="secondary" onClick={() => setDebouncedSearch(searchQuery)}>
                {t('payments.filters.searchApply')}
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="instapay-status-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.instapay.filters.reviewStatus')}
            </label>
            <select
              id="instapay-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InstapayReviewStatus | '')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('payments.instapay.filters.allReviewStatuses')}</option>
              {INSTAPAY_REVIEW_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {t(`payments.instapay.reviewStatus.${status}`, status)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingPanel />
      ) : error ? (
        <ErrorPanel message={t('payments.instapay.error.loadFailed')} />
      ) : reviews.length === 0 ? (
        <EmptyPanel
          title={t('payments.instapay.empty.title')}
          message={t('payments.instapay.empty.message')}
        />
      ) : (
        <>
          <DataTable columns={columns} data={reviews} rowKey={(row) => row.payment_id} />
          <p className="text-xs text-muted-foreground">
            {t('payments.footer.showing', {
              shown: reviews.length,
              total: data?.total ?? reviews.length,
            })}
          </p>
        </>
      )}

      <InstapayReviewDetailDrawer
        review={selectedReview}
        locale={locale}
        canReview={canReview}
        onClose={() => setSelectedReview(null)}
        onUpdated={(item) => {
          setSelectedReview(item);
          void refetch();
        }}
      />
    </div>
  );
}
